import React from 'react';
import { Dialog } from '@headlessui/react';
import { Modal } from 'components/Modal';
import { Button } from 'components/Button';
import { SizeEnum } from 'utils';
import { VisualRulePipelineBuilder } from './ClaimAutomation/VisualRulePipelineBuilder';

type ClaimAutomationRuleModalProps = {
  show: boolean;
  setEditingRule: (value: any) => void;
  editingRule: any;
  addNewRule?: (value: string) => void;
  removeRule?: (index: number) => void;
  onChangeRuleValue?: (field: string, value: string, index: number) => void;
  handleSaveRule: (rule: any) => void;
  isLoading?: boolean;
  validationErrors: string | null;
};

export const ClaimAutomationRuleModal: React.FC<
  ClaimAutomationRuleModalProps
> = ({
  show,
  setEditingRule,
  editingRule,
  handleSaveRule,
  isLoading,
  validationErrors,
}) => {
  if (!editingRule) return null;

  return (
    <Modal
      openModal={() => setEditingRule(null)}
      open={show}
      closeDisabled={isLoading}
      containerClass="sm:max-w-4xl sm:w-full"
      customStyle={{ maxWidth: '68rem' }}
    >
      <div className="flex flex-col max-h-[calc(100vh-15rem)] overflow-hidden">
        {/* Modal Header */}
        <div className="border-b border-slate-200 pb-4 shrink-0">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
              Rule Configuration
            </span>
            <Dialog.Title
              as="h3"
              className="text-xl font-bold text-slate-900 mt-0.5"
            >
              {editingRule.id
                ? 'Edit Claim Automation'
                : 'New Claim Automation'}
            </Dialog.Title>
            <p className="text-xs text-slate-500 mt-1">
              Configure when and how incoming claims should be automatically
              resolved.
            </p>
          </div>

          {validationErrors && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs font-medium text-red-700 flex items-center gap-2">
              <span className="font-bold">Error:</span> {validationErrors}
            </div>
          )}
        </div>

        {/* Visual Pipeline Builder */}
        <div className="flex-1 min-h-0 overflow-y-auto py-4 pr-1 sm:pr-2 modal-pipeline-scroll">
          <fieldset disabled={isLoading}>
            <VisualRulePipelineBuilder
              rule={editingRule}
              onRuleChange={setEditingRule}
              disabled={isLoading}
            />
          </fieldset>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 shrink-0 bg-white">
          <button
            type="button"
            disabled={isLoading}
            onClick={() => setEditingRule(null)}
            className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <Button
            onClick={() => handleSaveRule(editingRule)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm px-6 py-2.5 rounded-lg"
            size={SizeEnum.lg}
            isLoading={isLoading}
          >
            {editingRule.id ? 'Update Rule' : 'Create Rule'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
